<template lang="pug">
  yz-dialog(v-if="showDialog" v-model="showDialog" fullscreen persistent scrollable)
    yz-card
      yz-dialog-fullscreen-header(:title="$t('panel.add_panel')" :cancel-action-label="$t('cancel')" :save-action-label="$t('save')" :loading="isLoading" @back="closeDialog" @cancel="closeDialog" @save="savePanel")
      yz-dialog-fullscreen-content.pa-0
        div(style="padding: 24px 68px")
          v-form(ref="form" v-model="isFormValid")
            v-row(no-gutters)
              // Left Column - Panel Information & Contact Information
              v-col.text-neutral-700(cols="7")
                .yz-heading-9 {{ $t('panel.panel_information') }}
                .yz-regular-body-xs {{ $t('panel.fill_panel_info_below') }}

                v-row.mt-6(no-gutters)
                  // Left side of left panel - Panel Information Group
                  v-col.pr-4
                    .yz-medium-body-sm.mb-3 {{ $t('panel.panel_type') }}

                    yz-radio-group(v-model="panelType" direction="horizontal" required :required-error-message="$t('panel.payment_method_required')")
                      yz-radio(value="tpa" :label="$t('panel.third_party_administrator')")
                      yz-radio(value="direct" :label="$t('panel.direct_company')")

                    yz-card.pa-3.mt-2(color="neutral-300")
                      .yz-medium-body-xs(style="margin-bottom: 2px") {{ $t('panel.submission_delivery_preference') }}
                      .yz-regular-body-xs {{ $t('panel.set_how_claims_delivered') }}
                      yz-radio-group.mt-2(v-model="submissionType" direction="horizontal" required :required-error-message="$t('panel.payment_method_required')")
                        yz-radio(value="single" :label="$t('panel.single_claim')" label-class="yz-regular-body-xs")
                        yz-radio(value="bulk" :label="$t('panel.bulk_claim')" label-class="yz-regular-body-xs")

                    .yz-medium-body-sm.mt-5 {{ $t('panel.company_registration_number') }}
                    .yz-regular-body-xs.text-neutral-500 {{ $t('panel.company_registration_number_description') }}
                    yz-text-field.mt-1(v-model="panelRegistrationNumber" placeholder="e.g. 202201234567" maxlength="12" counter, :rules="[...rules.ssm_digits,...rules.ssm_length]")

                    .yz-medium-body-sm.mt-5 {{ $t('panel.company_name') }}
                    yz-text-field.mt-1(v-model="panelCompanyName" :placeholder="$t('enter') + ' ' + $t('panel.company_name').toLowerCase()")

                    .d-flex.align-end.mt-5.gap-1
                      .yz-medium-body-sm {{ $t('panel.panel_name') }}
                      yz-tooltip(:text="$t('panel.panel_name_tooltip')" direction="right" max-width="320px")
                        yz-icon.cursor-help(size="16") $yzi-info-circle
                    yz-text-field.mt-1(v-model="panelName" :placeholder="$t('enter') + ' ' + $t('panel.panel_name').toLowerCase()" required, :required-error-message="$t('panel.payment_method_required')")

                    .yz-medium-body-sm.mt-5 {{ $t('panel.panel_code') }}
                    yz-text-field.mt-1(v-model="panelCode" counter maxlength="8" :placeholder="$t('enter') + ' ' + $t('panel.panel_code').toLowerCase()" @input="onInputCode")

                    .yz-medium-body-sm.mt-5 {{ $t('panel.person_in_charge') }}
                    yz-text-field.mt-1(v-model="panelPicName" :placeholder="$t('enter') + ' ' + $t('panel.person_in_charge').toLowerCase()")

                  // Right side of left panel - Contact Information Group
                  v-col.pl-4
                    .yz-medium-body-sm.mb-2 {{ $t('phone_number') }}
                    yz-phone-input.mb-4(v-model="panelPicPhone" :placeholder="$t('enter') + ' ' + $t('phone_number').toLowerCase()")

                    .yz-medium-body-sm.mb-2 {{ $t('email') }}
                    yz-text-field.mb-4(v-model="panelEmail" :placeholder="$t('enter') + ' ' + $t('email').toLowerCase()" :rules="panelEmail && panelEmail.trim().length > 0 ? rules.valid_email : []")

                    .d-flex.align-center.justify-space-between.mb-2
                      .yz-medium-body-sm {{ $t('address') }}
                      yz-btn(v-if="hasAddressData" text size="sm" :label="$t('clear')" @click="onResetAddress")
                    yz-address-input(:line1.sync="panelAddress.line1" :line2.sync="panelAddress.line2" :postcode.sync="panelAddress.postcode" :city.sync="panelAddress.city" :state.sync="panelAddress.state" :country.sync="panelAddress.country")

              yz-divider.mx-8(vertical)

              // Right Column - Verification & Panel Rules
              v-col.text-neutral-700
                .yz-heading-9 {{ $t('panel.panel_verification') }}
                .yz-regular-body-xs {{ $t('panel.provide_link_for_users') }}

                yz-radio-group.mt-5(v-model="verificationType" direction="horizontal")
                  yz-radio(value="url" :label="$t('panel.url_to_panel_platform')")
                  yz-radio(value="remarks" :label="$t('panel.manual_remark')")

                yz-card.pa-3.mt-2(v-if="verificationType" color="neutral-300")
                  template(v-if="verificationType === 'url'")
                    .yz-medium-body-sm {{ $t('panel.verification_link') }}
                    yz-text-field.mt-1(v-model="panelVerificationLink" size="lg" :placeholder="$t('enter') + ' ' + $t('panel.verification_link').toLowerCase()")
                  template(v-if="verificationType === 'remarks'")
                    .yz-medium-body-sm {{ $t('panel.remarks') }}
                    yz-textarea.mt-1(v-model="panelRemarks" :placeholder="$t('enter') + ' ' + $t('panel.remarks').toLowerCase()")

                .yz-heading-9.mt-6 {{ $t('panel.panel_rule') }}
                .yz-regular-body-xs {{ $t('panel.set_operational_terms') }}

                .yz-medium-body-sm.mt-6 {{ $t('panel.claim_submission_due_date') }}
                yz-radio-group.mt-3(v-model="deadlineType" direction="horizontal" required :required-error-message="$t('panel.date_required')")
                  yz-radio(value="fixed" :label="$t('panel.fixed_deadline')")
                  yz-radio(value="relative" :label="$t('panel.relative_deadline')")

                yz-card.pa-3.mt-2(v-if="deadlineType" color="neutral-300")
                  template(v-if="deadlineType === 'fixed'")
                    .d-flex.align-center.gap-3.flex-wrap
                      .yz-regular-body-sm {{ $t('submit') }}
                      yz-text-field(v-model="fixedTimeValue" type="number" size="md" placeholder="30" width="80px" required :required-error-message="$t('panel.amount_required')")
                      yz-dropdown(v-model="fixedTimeUnit" :placeholder="$t('select') + ' unit'" :items="timeUnitsFormatted" size="md" width="130px" required :required-error-message="$t('panel.payment_method_required')")
                      .yz-regular-body-sm {{ $t('panel.after_invoice_finalised') }}

                  template(v-if="deadlineType === 'relative'")
                    .d-flex.align-center.gap-3.mb-3.flex-wrap
                      .yz-regular-body-sm {{ $t('submit') }}
                      yz-dropdown(v-model="submitFrequency" :placeholder="$t('select') + ' frequency'" :items="frequencyOptionsFormatted" size="md" width="120px" required :required-error-message="$t('panel.payment_method_required')")
                      .yz-regular-body-sm {{ $t('panel.by') }}

                      // Daily Frequency - show time and same day option
                      template(v-if="submitFrequency === 'daily'")
                        yz-text-field(v-model="submitTime" size="md" placeholder="11:59" type="time" width="130px" required :required-error-message="$t('panel.amount_required')")
                        yz-dropdown(v-model="sameDayOption" :items="sameDayOptionsFormatted" size="md" width="180px" :placeholder="$t('select') + ' day'" required :required-error-message="$t('panel.date_required')")

                      // Weekly Frequency - show day and time
                      template(v-if="submitFrequency === 'weekly'")
                        yz-text-field(v-model="submitTime" size="md" placeholder="11:59" type="time" width="130px" required :required-error-message="$t('panel.amount_required')")
                        yz-dropdown(v-model="weeklyDay" :items="weeklyDayOptionsFormatted" size="md" width="180px" :placeholder="$t('select') + ' day'" required :required-error-message="$t('panel.date_required')")

                      // Monthly Frequency - show monthly day and time
                      template(v-if="submitFrequency === 'monthly'")
                        yz-text-field(v-model="submitTime" size="md" placeholder="11:59" type="time" width="130px" required :required-error-message="$t('panel.amount_required')")
                        yz-dropdown(v-model="monthlyDay" :items="monthlyDayOptionsFormatted" size="md" width="180px" :placeholder="$t('select') + ' month day'" required :required-error-message="$t('panel.date_required')")

                .yz-heading-9.mt-6 {{ $t('panel.select_price_tier') }}
                .d-flex.align-center
                  .yz-regular-body-xs {{ $t('panel.manage_pricing_tier') }}
                  yz-btn(text underline size="sm" :label="$t('panel.pricing_tier_setting')" append-icon="$yzi-arrow-up-right" icon-size="13" @click="handleGoToPriceTierSetting")

                .yz-medium-body-sm.mt-5 {{ $t('panel.price_tier') }}
                price-tier-dropdown.mt-1(v-model="selectedPriceTier" size="lg" required)

                .yz-heading-9.mt-6 {{ $t('panel.e_invoice_setting') }}
                .yz-regular-body-xs {{ $t('panel.e_invoice_setting_description') }}

                .yz-medium-body-sm.mt-5 {{ $t('panel.income_tax_number_tin') }}
                yz-text-field.mt-1(v-model="panelTin" placeholder="Example: C1234567" size="lg")

          template(v-if="parentPanel")
            yz-divider.my-8

            // Visits Table Section
            .mt-8
              yz-tabs(v-model="tab")
                yz-tab(:label="$t('panel.visits')")
                yz-tab(:label="$t('panel.eligible_staffs')")

              .mt-4(style="min-height: 400px")
                template(v-if="tab === 0")
                  visits-table(:parent-panel-id="parentPanelId")
                template(v-if="tab === 1")
                  eligible-staff-table(:parent-panel-id="parentPanelId")
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { capitalize } from '@/plugins/filters'
import PriceTierDropdown from '@/components/clinic/panel/PriceTierDropdown.vue'
import VisitsTable from '@/components/clinic/panel/settings/VisitsTable.vue'
import EligibleStaffTable from '@/components/clinic/panel/settings/EligibleStaffTable.vue'

export default {
  components: {
    EligibleStaffTable,
    PriceTierDropdown,
    VisitsTable
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    parentPanel: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,

      // Panel properties
      panelName: '',
      panelCode: '',
      panelPicName: '',
      panelPicPhone: '',
      panelEmail: '',
      panelRegistrationNumber: '',
      panelCompanyName: '',
      panelVerificationLink: '',
      panelRemarks: '',

      // Panel address properties
      panelAddress: {
        line1: '',
        line2: '',
        postcode: '',
        city: '',
        state: '',
        country: ''
      },

      // Panel type, submission type, verification type, deadline type
      panelType: null,
      submissionType: 'bulk',
      verificationType: 'url',
      deadlineType: null,

      // Fixed deadline properties
      fixedTimeValue: null,
      fixedTimeUnit: null,

      // Relative deadline properties
      submitFrequency: null,
      submitTime: null,
      sameDayOption: null,
      weeklyDay: null,
      monthlyDay: null,

      // Price Tier
      selectedPriceTier: [],

      // E-Invoice
      panelTin: null,

      // Fixed deadline options
      timeUnits: ['minutes', 'hours', 'days'],

      // Relative deadline options
      frequencyOptions: ['daily', 'weekly', 'monthly'],
      sameDayOptions: ['Same day', 'Next day', '2 days after', '3 days after'],
      weeklyDayOptions: ['Next Monday', 'Next Tuesday', 'Next Wednesday', 'Next Thursday', 'Next Friday', 'Next Saturday', 'Next Sunday'],
      monthlyDayOptions: ['1st day of next month', '2nd day of next month', '3rd day of next month', '4th day of next month', 'Last day of the month'],
      // Form validation
      isFormValid: false,

      // Validation rules
      rules: {
        valid_email: [v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || this.$t('email_must_be_valid')],
        ssm_length: [v => !v || v.length === 12 || this.$t('panel.ssm_must_be_12_characters')],
        ssm_digits: [v => !v || /^\d+$/.test(v) || this.$t('panel.ssm_must_be_digits')]
      },

      tab: 0
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      stateOptions: 'getStates'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    frequencyOptionsFormatted() {
      return this.frequencyOptions.map(option => ({ text: capitalize(option), value: option }))
    },
    sameDayOptionsFormatted() {
      return this.sameDayOptions.map(option => ({ text: option, value: option }))
    },
    stateOptionsFormatted() {
      return this.stateOptions.map(option => ({ text: option, value: option }))
    },
    timeUnitsFormatted() {
      return this.timeUnits.map(unit => ({ text: capitalize(unit), value: unit }))
    },
    weeklyDayOptionsFormatted() {
      return this.weeklyDayOptions.map(day => ({ text: day, value: day }))
    },
    monthlyDayOptionsFormatted() {
      return this.monthlyDayOptions.map(option => ({ text: option, value: option }))
    },
    parentPanelId() {
      return this.parentPanel?.panel?.id ?? null
    },
    hasAddressData() {
      return !this.isPanelAddressEmpty()
    }
  },
  mounted() {
    if (this.parentPanel) {
      this.setPanelData()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setPanelData() {
      const parentPanel = this.parentPanel
      const panelData = parentPanel.panel || {}

      // Basic Info: Use parentPanel.name, prioritize parentPanel.panel.code over parentPanel.code
      this.panelName = parentPanel.name || ''
      this.panelCode = panelData.code || parentPanel.code || ''

      // Contact: Prioritize parentPanel.panel.pic_name and parentPanel.panel.phone over parent equivalents
      this.panelPicName = panelData.pic_name || parentPanel.pic_name || ''
      this.panelPicPhone = panelData.phone || parentPanel.pic_phone || ''
      this.panelEmail = panelData.email || ''

      // Company: Use parentPanel.panel.company_name and parentPanel.panel.registration_number
      this.panelCompanyName = panelData.company_name || ''
      this.panelRegistrationNumber = panelData.registration_number || ''

      // Address: Prioritize parentPanel.panel.address over parentPanel.extra_data.address
      const addressSource = panelData.address || parentPanel.extra_data?.address || {}
      this.panelAddress = {
        line1: addressSource.address_1 || '',
        line2: addressSource.address_2 || '',
        city: addressSource.city || '',
        postcode: addressSource.postcode || '',
        state: addressSource.state || '',
        country: addressSource.country || ''
      }

      // Panel Config: Use parentPanel.panel.type, parentPanel.panel.submission_type
      this.panelType = panelData.type || null
      this.submissionType = panelData.submission_type || null

      // Verification: Set verificationType based on parentPanel.panel.display_remarks flag
      this.verificationType = panelData.display_remarks ? 'remarks' : 'url'
      this.panelVerificationLink = panelData.verification_link || ''
      this.panelRemarks = panelData.remarks || ''

      // Deadlines: Use parentPanel.panel.submission_due_* fields for fixed deadlines
      this.deadlineType = panelData.submission_due_type || null

      // Fixed deadline fields
      if (this.deadlineType === 'fixed') {
        this.fixedTimeValue = panelData.submission_due_value || null
        this.fixedTimeUnit = panelData.submission_due_unit || null
      }

      // Relative deadline fields - map from backend format to UI format
      if (this.deadlineType === 'relative') {
        this.setRelativeDeadlineFromData(panelData)
      }

      // Price Tiers: Use parentPanel.price_tiers
      this.selectedPriceTier = parentPanel.price_tiers || []

      // E-Invoice: Use parentPanel.panel.tin
      this.panelTin = panelData.tin || null
    },
    setRelativeDeadlineFromData(panelData) {
      this.submitFrequency = panelData.submission_due_unit || null
      this.submitTime = panelData.submission_due_time || null

      if (panelData.submission_due_unit === 'daily') {
        const sameDayReverseMapping = {
          0: 'Same day',
          1: 'Next day',
          2: '2 days after',
          3: '3 days after'
        }
        this.sameDayOption = sameDayReverseMapping[panelData.submission_due_value] || null
        return
      }
      if (panelData.submission_due_unit === 'weekly') {
        const weeklyDayReverseMapping = {
          1: 'Next Sunday',
          2: 'Next Monday',
          3: 'Next Tuesday',
          4: 'Next Wednesday',
          5: 'Next Thursday',
          6: 'Next Friday',
          7: 'Next Saturday'
        }
        this.weeklyDay = weeklyDayReverseMapping[panelData.submission_due_value] || null
        return
      }
      if (panelData.submission_due_unit === 'monthly') {
        const monthlyDayReverseMapping = {
          1: '1st day of next month',
          2: '2nd day of next month',
          3: '3rd day of next month',
          4: '4th day of next month',
          31: 'Last day of the month'
        }
        this.monthlyDay = monthlyDayReverseMapping[panelData.submission_due_value] || null
      }
    },
    getRelativeDeadlineValue() {
      if (this.submitFrequency === 'daily') {
        const sameDayMapping = {
          'Same day': 0,
          'Next day': 1,
          '2 days after': 2,
          '3 days after': 3
        }
        return sameDayMapping[this.sameDayOption] ?? null
      }

      if (this.submitFrequency === 'weekly') {
        const weeklyDayMapping = {
          'Next Sunday': 1,
          'Next Monday': 2,
          'Next Tuesday': 3,
          'Next Wednesday': 4,
          'Next Thursday': 5,
          'Next Friday': 6,
          'Next Saturday': 7
        }
        return weeklyDayMapping[this.weeklyDay] ?? null
      }

      if (this.submitFrequency === 'monthly') {
        const monthlyDayMapping = {
          '1st day of next month': 1,
          '2nd day of next month': 2,
          '3rd day of next month': 3,
          '4th day of next month': 4,
          'Last day of the month': 31
        }
        return monthlyDayMapping[this.monthlyDay] ?? null
      }

      return null
    },
    getDeadlinePayload() {
      if (this.deadlineType === 'fixed') {
        return {
          submission_due_value: this.fixedTimeValue,
          submission_due_unit: this.fixedTimeUnit,
          submission_due_time: null
        }
      }

      if (this.deadlineType === 'relative') {
        return {
          submission_due_value: this.getRelativeDeadlineValue(),
          submission_due_unit: this.submitFrequency,
          submission_due_time: this.submitTime
        }
      }

      return {
        submission_due_value: null,
        submission_due_unit: null,
        submission_due_time: null
      }
    },
    isPanelAddressEmpty() {
      return !this.panelAddress.line1 && !this.panelAddress.line2 && !this.panelAddress.city && !this.panelAddress.postcode && !this.panelAddress.state && !this.panelAddress.country
    },
    getAddressPayload() {
      if (this.isPanelAddressEmpty()) {
        return null
      }
      return {
        address_1: this.panelAddress.line1,
        address_2: this.panelAddress.line2,
        city: this.panelAddress.city,
        postcode: this.panelAddress.postcode,
        state: this.panelAddress.state,
        country: this.panelAddress.country
      }
    },
    createPayload() {
      const deadlineParams = this.getDeadlinePayload()

      return {
        company: this.company.id,
        name: this.panelName,
        code: this.panelCode,
        pic_name: this.panelPicName,
        phone: this.panelPicPhone,
        email: this.panelEmail,
        company_name: this.panelCompanyName,
        registration_number: this.panelRegistrationNumber,
        address: this.getAddressPayload(),
        type: this.panelType,
        submission_type: this.submissionType,
        display_remarks: this.verificationType === 'remarks',
        remarks: this.verificationType === 'url' ? null : this.panelRemarks,
        verification_link: this.verificationType === 'remarks' ? null : this.panelVerificationLink,
        submission_due_type: this.deadlineType,
        ...deadlineParams,
        price_tiers: this.selectedPriceTier.map(priceTier => priceTier.id),
        tin: this.panelTin || null
      }
    },
    async savePanel() {
      const validForm = this.$refs.form.validate()

      // Validate form using v-form
      if (!validForm) {
        await this.showMessage(['error', this.$t('panel.fill_required_fields')])
        return
      }

      this.isLoading = true
      const payload = this.createPayload()
      try {
        if (this.parentPanel) {
          await this.$axios.patch(`/panels/${this.parentPanel.panel.id}`, payload)
        } else {
          await this.$axios.post('/panels', payload)
        }
        this.$emit('reset')
        await this.showMessage(['success', this.parentPanel ? this.$t('success_message.panel_updated') : this.$t('success_message.panel_created')])
        this.showDialog = false
      } catch (error) {
        const errorCode = error?.response?.status
        const verificationError = error?.response?.data?.verification_link
        if (errorCode === 400 && verificationError[0].includes('Enter a valid URL')) {
          await this.showMessage(['error', this.$t('panel.please_enter_valid_url')])
          return
        }
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    closeDialog() {
      this.$emit('closed')
      this.showDialog = false
    },
    onInputCode(value) {
      this.panelCode = value.toUpperCase()
    },
    handleGoToPriceTierSetting() {
      this.$router.push('/admin/clinic/settings/price-tier-settings')
    },
    onResetAddress() {
      this.panelAddress = {
        line1: '',
        line2: '',
        city: '',
        postcode: '',
        state: '',
        country: ''
      }
    }
  }
}
</script>

<style scoped>
</style>
